'use client';

import { type ReactNode } from 'react';
import { cn } from '@core/helpers/misc/GeneralUtils';

/**
 * AppNavBar - Global three-zone navigation bar component
 *
 * NOTE: The navbar uses a FIXED max-width of 1440px, not affected by user's
 * layout width setting. This ensures consistent navigation regardless of
 * content width preferences.
 *
 * Structure (Wide ≥1440px):
 * ┌──────────────────────────────────────────────────────────────────┐
 * │ max-width: 1440px (fixed), centered                              │
 * │ grid-cols-[240px_1fr_240px]                                      │
 * │ [LEFT 240px]  [CENTER max 960px]  [RIGHT 240px]                  │
 * └──────────────────────────────────────────────────────────────────┘
 *
 * Structure (Desktop 1024-1439px):
 * ┌──────────────────────────────────────────────────────────────────┐
 * │ full width                                                        │
 * │ grid-cols-[240px_1fr_240px]                                      │
 * │ [LEFT 240px]  [CENTER flexible]  [RIGHT 240px]                   │
 * └──────────────────────────────────────────────────────────────────┘
 *
 * Structure (Mobile <1024px):
 * ┌──────────────────────────────────────────────────────────────────┐
 * │ full width, flex justify-between                                  │
 * │ [LEFT]                                              [RIGHT]       │
 * └──────────────────────────────────────────────────────────────────┘
 *
 * Uses tokens:
 * - bg-background-blur: nav background with transparency for backdrop-blur
 * - border-border-subtle: subtle border color
 */

interface AppNavBarProps {
  /** Content for the left zone (sidebar trigger, org selector) */
  leftContent?: ReactNode;
  /** Content for the center zone (breadcrumb, navigation, action bar) */
  centerContent?: ReactNode;
  /** Content for the right zone (search, settings, user menu) */
  rightContent?: ReactNode;
  /** Additional className for the nav container */
  className?: string;
}

export function AppNavBar({ leftContent, centerContent, rightContent, className }: AppNavBarProps) {
  return (
    <nav
      className={cn(
        'app-nav-bar fixed top-0 left-0 right-0 z-50 h-16',
        'backdrop-blur-xl bg-main-background/30',
        'border-b border-white/5',
        'transition-colors duration-300',
        className
      )}
      // iOS Safari fix: -webkit-backdrop-filter (autoprefixer/Lightning CSS don't add it reliably)
      style={{ WebkitBackdropFilter: 'blur(24px)' }}>
      <div
        className={cn(
          'navbar-inner h-full',
          'transition-all duration-300 ease-out',
          // Wide: centered container with fixed max-width (not affected by user setting), 3-col grid
          'wide:max-w-[1440px] wide:mx-auto wide:grid wide:grid-cols-[240px_1fr_240px]',
          // Desktop: full width, 3-col grid (center visible)
          'desktop-only:grid desktop-only:grid-cols-[240px_1fr_240px]',
          // Mobile: flex (center hidden)
          'max-mobile:flex max-mobile:justify-between max-mobile:px-4'
        )}>
        {/* Left Zone - 240px on desktop+, flexible on mobile */}
        <div className='navbar-left flex items-center gap-1 pl-2 pr-4 max-mobile:px-0'>{leftContent}</div>

        {/* Center Zone - visible on Wide/Desktop, hidden on Mobile */}
        <div
          className={cn(
            'navbar-center flex items-center justify-center min-w-0',
            'transition-all duration-300 ease-out',
            // Wide: max-width matches content area (1440 - 480 = 960px)
            'wide:max-w-[960px] wide:mx-auto wide:w-full',
            // Desktop: fills the center grid cell
            'desktop-only:w-full',
            // Mobile: hidden
            'max-mobile:hidden'
          )}>
          {centerContent}
        </div>

        {/* Right Zone - 240px on desktop+, flexible on mobile */}
        <div className='navbar-right flex items-center justify-end gap-1 px-4 max-mobile:gap-1 max-mobile:px-0'>
          {rightContent}
        </div>
      </div>
    </nav>
  );
}

export default AppNavBar;
